// Money, dates and decimals-as-strings, in the current language.

/** `currency` is ISO 4217, always the one the amount is in: an order's, an offer's, the organization's. */
export function money(value: number | string, currency: string, locale = 'en'): string {
  const n = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(n)) return String(value);
  return new Intl.NumberFormat(locale === 'fr' ? 'fr-FR' : 'en-US', { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: currency === 'USD' && n < 1 ? 4 : 2 }).format(n);
}

/** "€", "$", "£" (fr: "$US") — for a label that says what an amount field is in. */
export function currencySymbol(currency: string, locale = 'en'): string {
  const parts = new Intl.NumberFormat(locale === 'fr' ? 'fr-FR' : 'en-US', { style: 'currency', currency }).formatToParts(0);
  return parts.find((p) => p.type === 'currency')?.value ?? currency;
}

export function date(value: string | null | undefined, locale = 'en'): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat(locale === 'fr' ? 'fr-FR' : 'en-GB', { year: 'numeric', month: 'short', day: 'numeric' }).format(d);
}

/** A decimal string for the API from a form input ("12,5" → "12.50"); null when blank or not a number. */
export function toDecimal(input: string): string | null {
  const t = input.trim().replace(',', '.');
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 ? n.toFixed(2) : null;
}

/** Share of the allowance used, 0..1, capped; 0 when unlimited or no allowance. */
export function usageRatio(spent: number, budget: number, unlimited: boolean): number {
  if (unlimited || budget <= 0) return 0;
  return Math.min(1, Math.max(0, spent / budget));
}

/** A plain member sees only their own usage and limit, never the team's: the API says so with `scope: 'member'`;
 *  an older API without `scope` is read from the role. */
export function ownUsageOnly(usage: { scope?: 'organization' | 'member' }, role: string | null | undefined): boolean {
  return usage.scope === 'member' || role === 'member';
}
